import { queryKeys } from '@query/keys'
import { PaginatedQueryResult, usePaginatedQuery } from '@query/usePaginatedQuery'
import { UseMutateFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@app/context/SnackbarContext'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { ExportHubDto, ExportHubReportStatusId } from '@model/ExportHub'

import { SearchResponseProps } from '../../IBReportResultsPage'

interface UseExportHubProps {
  search: SearchResponseProps
}

const POLLING_WHILE_PROCESSING_STATUS = 10_000

export const useExportHub = (
  props: UseExportHubProps
): {
  query: PaginatedQueryResult<ExportHubDto>
  retryReport: UseMutateFunction<void, Error, string, unknown>
  isRetryPending: boolean
} => {
  const { search } = props
  const { account } = useAccountReadContext()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const inFlightIdsRef = useRef<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const ibId = account?.clientIntroducingBroker?.id

  const searchKey = {
    ...search.search,
    ca_search_ReportName: search.debouncedSearchTerm,
    locale,
  }

  const query = usePaginatedQuery<ExportHubDto>({
    queryKey: queryKeys.ib.exportHub.list(ibId, searchKey, locale),
    enabled: !!ibId,
    queryFn: async (pageQuery) => {
      return apiClient.getExportHubReports(ibId!, {
        ...pageQuery,
        search: {
          ...search.search,
          ca_search_ReportName: search.debouncedSearchTerm,
        },
        sort: pageQuery?.sort,
        sortOrder: pageQuery?.sortOrder,
        caller: 'ca',
        languageId: locale,
      })
    },
    refetchInterval: (query) => {
      const items = query.state.data?.data ?? []
      return items.some((r) => r.status === ExportHubReportStatusId.Processing)
        ? POLLING_WHILE_PROCESSING_STATUS
        : false
    },
  })

  const acknowledgeMutation = useMutation({
    mutationFn: (reportIds: string[]) => {
      if (!ibId) {
        throw new Error('introducingBrokerId is missing')
      }

      return apiClient.exportHubAcknowledge(ibId ?? '', { reportIds })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ib.exportHubHasUnseen(ibId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.ib.exportHub.list(ibId, searchKey, locale),
      })
    },
    onSettled: (_data, _error, reportIds) => {
      reportIds.forEach((id) => inFlightIdsRef.current.delete(id))
    },
  })

  const { mutate: retryReport, isPending: isRetryPending } = useMutation({
    mutationFn: async (id: string) => apiClient.retryExportHubReport(ibId ?? '', id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ib.exportHub.list(ibId, searchKey, locale),
      })
      addSnackbar.success({ message: t('IB.Export.retrySuccess') })
    },
  })

  useEffect(() => {
    const items = query.data
    if (!items?.length) return

    const idsToAcknowledge = items
      .filter((item) => item.acknowledgedAt === null)
      .map((item) => item.id)
      .filter((id) => !inFlightIdsRef.current.has(id))

    if (idsToAcknowledge.length === 0) return

    idsToAcknowledge.forEach((id) => inFlightIdsRef.current.add(id))
    acknowledgeMutation.mutate(idsToAcknowledge)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data])

  return { query, retryReport, isRetryPending }
}
